import { apiClient } from '../lib/apiClient';
import type {
  ImagePresignRequest,
  ImagePresignResponse,
} from '../components/common/ImageAddDialog/imageUpload';

type PresignResponseItem = {
  key: string;
  uploadUrl: string;
  uploadHeaders?: Record<string, string>;
  contentType?: string;
  bucket?: string;
  fileName?: string;
};

type PresignBatchResponse = {
  files: PresignResponseItem[];
  expiresIn?: number;
};

export type ImageUploadDbRecord = {
  wrId: number;
  key: string;
  bucket?: string;
  fileName?: string;
  contentType?: string;
  sizeBytes?: number;
  orderNumber?: number;
};

const hasContentTypeHeader = (headers: Record<string, string> | undefined): boolean =>
  !!headers && Object.keys(headers).some((key) => key.toLowerCase() === 'content-type');

export const postPresignedUploadUrls = async (
  files: ImagePresignRequest[],
  wrId?: number,
  signal?: AbortSignal
): Promise<ImagePresignResponse[]> => {
  const payload: {
    wr_id?: number;
    files: Array<{ fileName: string; contentType: string }>;
  } = {
    files: files.map(({ fileName, contentType }) => ({ fileName, contentType })),
  };

  if (typeof wrId === 'number') {
    payload.wr_id = wrId;
  }

  const res = await apiClient.post<PresignBatchResponse>(
    '/s3_presigned_upload',
    payload,
    { signal }
  );

  return (res.data.files ?? []).map((item, index) => {
    const contentType = item.contentType || files[index]?.contentType || 'application/octet-stream';
    const uploadHeaders = {
      ...(item.uploadHeaders ?? {}),
      ...(!hasContentTypeHeader(item.uploadHeaders) ? { 'Content-Type': contentType } : {}),
    };

    return {
      fileKey: item.key,
      uploadUrl: item.uploadUrl,
      uploadMethod: 'PUT',
      uploadHeaders,
      contentType,
      bucket: item.bucket,
      fileName: item.fileName,
    };
  });
};

export const postUploadMultiFileManage = async (
  payload: {
    wr_id: number;
    files: Array<{
      file_id?: number | null;
      bucket?: string;
      key?: string;
      fileName?: string;
      contentType?: string;
      size_bytes?: number;
      order_number: number;
    }>;
    create_by?: string;
    update_by?: string;
  },
  signal?: AbortSignal
): Promise<void> => {
  const res = await apiClient.post(
    '/add_upload_multifile_manage',
    payload,
    { signal }
  );

  if (res.data === false) {
    throw new Error('Image metadata was not saved');
  }
};

export type UploadedImageFileRecord = {
  file_id: number;
  wr_id: number;
  object_key: string;
};

/** Read persisted IDs. Never match screenshots by their often-identical names. */
export const getUploadedImageFileRecords = async (
  wrId: number,
  signal?: AbortSignal
): Promise<UploadedImageFileRecord[]> => {
  if (!Number.isSafeInteger(wrId) || wrId <= 0) {
    throw new Error('A valid wr_id is required');
  }

  const res = await apiClient.get<unknown>('/get_upload_file_manage', {
    params: { wr_id: wrId, status: 'Y' },
    signal,
    timeout: 15000,
  });

  // A failed or malformed response must not be treated as an empty file list.
  if (!Array.isArray(res.data)) {
    throw new Error('Invalid image metadata response');
  }

  return res.data.flatMap((value: unknown): UploadedImageFileRecord[] => {
    if (!value || typeof value !== 'object') {
      throw new Error('Invalid image metadata record');
    }

    const row = value as Record<string, unknown>;
    if (row.status != null && row.status !== 'Y') return [];

    const fileId = Number(row.file_id);
    const reportId = Number(row.wr_id);
    const key = row.object_key;

    if (
      !['number', 'string'].includes(typeof row.file_id) ||
      !['number', 'string'].includes(typeof row.wr_id) ||
      !Number.isSafeInteger(fileId) || fileId <= 0 ||
      reportId !== wrId || typeof key !== 'string' || !key
    ) {
      throw new Error('Image metadata is missing a valid ID or key');
    }

    return [{ file_id: fileId, wr_id: reportId, object_key: key }];
  });
};